import { useEffect, useRef, useState } from 'react';

export interface RowAction {
  label: string;
  onSelect: () => void;
  tone?: 'danger';
  disabled?: boolean;
}

/**
 * Overflow (⋯) menu for secondary or destructive row actions, so tables keep
 * one visible action per row. The popover is position:fixed because table
 * wrappers scroll horizontally and would clip an absolutely positioned menu.
 */
export function RowActionsMenu({
  label,
  actions,
}: {
  /** Accessible name of the trigger, e.g. "ตัวเลือกเพิ่มเติม: น้ำดื่ม". */
  label: string;
  actions: RowAction[];
}) {
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const open = pos !== null;

  useEffect(() => {
    if (!open) return;
    const close = () => setPos(null);
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      close();
      triggerRef.current?.focus();
    };
    // Any scroll moves the trigger away from the fixed popover.
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggle = () => {
    if (open) return setPos(null);
    const r = triggerRef.current?.getBoundingClientRect();
    if (!r) return;
    setPos({ top: r.bottom + 4, right: window.innerWidth - r.right });
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={toggle}
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-btn text-ink-3 transition-colors hover:bg-bg-warm hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-ink"
      >
        <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5">
          <circle cx="5" cy="12" r="1.75" />
          <circle cx="12" cy="12" r="1.75" />
          <circle cx="19" cy="12" r="1.75" />
        </svg>
      </button>
      {pos && (
        <>
          <div
            className="fixed inset-0 z-40"
            aria-hidden="true"
            onClick={() => setPos(null)}
          />
          <div
            role="menu"
            style={{ top: pos.top, right: pos.right }}
            className="fixed z-50 min-w-[10rem] rounded-card border border-line-2 bg-surface p-1.5 text-left shadow-modal"
          >
            {actions.map((action) => (
              <button
                key={action.label}
                type="button"
                role="menuitem"
                disabled={action.disabled}
                onClick={() => {
                  setPos(null);
                  action.onSelect();
                }}
                className={`flex min-h-11 w-full items-center rounded-btn px-3 text-body font-medium transition-colors disabled:opacity-50 ${
                  action.tone === 'danger'
                    ? 'text-danger hover:bg-danger-light'
                    : 'text-ink hover:bg-bg-warm'
                }`}
              >
                {action.label}
              </button>
            ))}
          </div>
        </>
      )}
    </>
  );
}
